import { CheckableColl } from 'src/models/CheckableColl'
import CheckableMap from 'src/models/CheckableMap'
import ExtendedMap from 'src/models/ExtendedMap'

import teacherApi from '@/api/teacher.api'
import { MAX_REQUEST_NUMBER } from '@/constant/common'
import { LevelSetting, LevelSettingColl, Teacher, User } from '@/dto'
import { ReqParams } from '@/dto/common/ReqParams'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'
import queryClient from './queryClient'

class TeacherController implements Controller {
  adapter = {
    teacher: makeAdapter({
      from(data: res.Teacher): Teacher {
        return new Teacher(data)
      },
      toCreate(data: Teacher): req.CreateTeacher {
        const { name, phone, loginId, type } = data
        return { name, phone, loginId, type, password: loginId } //TODO: 추후에 password 입력칸 만들면 바꿔야함
      },
      toModify(data: Teacher): req.PatchTeacher {
        const { name, phone, type, academyName } = data
        return { name, phone, type, academyName }
      },
      toId(data: Teacher): req.TeacherId {
        if (data.teacherId === undefined) {
          throw new Error('선생님 아이디가 없습니다.')
        }
        return data.teacherId
      },
      toInitPassword(data: Teacher): req.InitTeacherPassword {
        return {
          relationId: data.teacherId,
          type: 'TEACHER',
          newPassword: data.loginId,
          newPasswordCheck: data.loginId,
        }
      },
    }),
    user: makeAdapter({
      from(teacher: res.Teacher): User {
        return new User({
          teacher,
        })
      },
    }),
    setting: makeAdapter({
      from({
        key,
        data,
      }: {
        key: 'veryLow' | 'low' | 'middle' | 'high' | 'veryHigh'
        data: res.TeacherSetting['high']['difficultySetting']
      }) {
        return new LevelSetting(key, data)
      },
    }),
  }
  fetchHasTeachers = async () => {
    const {
      data: {
        data: { content },
      },
    } = await teacherApi.getTeachers(new ReqParams({ params: { size: 1, page: 0 } }).toQueryString)

    return content.length > 0
  }

  fetchTeachers = async (pageQueryString: string) => {
    const {
      data: {
        data: { content, last },
      },
    } = await teacherApi.getTeachers(pageQueryString)

    return {
      teacherColl: new CheckableColl(
        CheckableMap.fromArr(content.map(this.adapter.teacher.from), 'teacherId' as keyof Teacher),
      ),
      last,
    }
  }

  // 전체 선생님 중
  fetchAllTeachers = () => {
    return queryClient.fetchQuery({
      queryKey: ['fetchAllTeachers'],
      queryFn: () =>
        this.fetchTeachers(
          new ReqParams({ params: { size: MAX_REQUEST_NUMBER, present: true } }).toQueryString,
        ),
    })
  }

  fetchUserTeacher = async (teacherId: req.TeacherId) => {
    const teacherRes = await teacherApi.getOneTeacher(teacherId)

    return this.adapter.user.from(teacherRes.data.data)
  }

  fetchTeacherSetting = async () => {
    const {
      data: { data },
    } = await teacherApi.getTeacherSetting()
    const levelSettingKeywords = ['veryLow', 'low', 'middle', 'high', 'veryHigh']

    const arr = Object.entries(data)
      .map(([key, value]) => {
        if (value && 'difficultySetting' in value && levelSettingKeywords.includes(key)) {
          return this.adapter.setting.from({
            key: key as 'veryLow' | 'low' | 'middle' | 'high' | 'veryHigh',
            data: value.difficultySetting,
          })
        }
      })
      .filter((v) => v)

    return {
      levelSetting: new LevelSettingColl(ExtendedMap.fromArr(arr as LevelSetting[], 'key')),
      worksheetSearchSetting: data.worksheetSearchSetting,
      workbookSearchSetting: data.workbookSearchSetting,
      labSetting: data.labSetting,
      quickMenuSetting: data.quickMenuSetting,
    }
  }

  fetchTeacherWorksheetFilterSetting = async () => {
    const {
      data: { data },
    } = await teacherApi.getTeacherSetting()

    return data
  }

  fetchTeacherLabSetting = async () => {
    const {
      data: { data },
    } = await teacherApi.getTeacherSetting()

    return data
  }

  updateTeacherSetting = async (data: req.TeacherSetting) => {
    await teacherApi.postTeacherSetting(data)
  }

  createTeacher = async (teacher: Teacher) => {
    await teacherApi.postTeacher(this.adapter.teacher.toCreate(teacher))
    queryClient.invalidateQueries({ queryKey: ['fetchAllTeachers'] })
  }

  modifyTeacher = async (teacher: Teacher) => {
    await teacherApi.patchTeacher(
      this.adapter.teacher.toId(teacher),
      this.adapter.teacher.toModify(teacher),
    )
    queryClient.invalidateQueries({ queryKey: ['fetchAllTeachers'] })
  }

  deleteTeachers = async (teacherIds: req.TeacherId[]) => {
    await teacherApi.deleteTeachers(teacherIds)
    queryClient.invalidateQueries({ queryKey: ['fetchAllTeachers'] })
  }

  initPassword = async (teacher: Teacher) => {
    await teacherApi.patchPassword(this.adapter.teacher.toInitPassword(teacher))
  }

  fetchActivityCount = async () => {
    const { data } = await teacherApi.getActivityCount()
    return data
  }
}

export const teacherController = new TeacherController()
